import { useClientPortalAPI } from "@certn/api";
import {
  DialogContent,
  DialogFooter,
  DialogHeader,
  Modal,
  PrimaryButton,
  Text,
} from "@certn/cascadia";
import { Dialog, Loader, SectionMessage } from "@certn/cascadia-react";

import { components } from "@certn/api/generated-schemas/client-portal";
import { css } from "@emotion/css";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCheckCardContext } from "../../CheckCardContext";
import { CriminalRecordsFullListModal } from "./CheckSpecific/CriminalRecordsFullListModal";
import { CommonFields } from "./CommonFields";
import { UmbrellaChecksSection } from "./UmbrellaChecksSection";
import { useSelectionEndpointData } from "./useSelectionEndpointData";
import {
  getCheckDisplayNameFromCheck,
  isInternationalCriminalCheck,
} from "./utils";

interface LearnMoreModalProps {
  show: boolean;
  onClose: () => void;
}

export function LearnMoreModal(props: LearnMoreModalProps) {
  return (
    <Modal isOpen={props.show} testID="learn-more-modal">
      <LearnMoreModalInner show={props.show} onClose={props.onClose} />
    </Modal>
  );
}

function LearnMoreModalInner(
  props: Pick<LearnMoreModalProps, "show" | "onClose">
) {
  const { onClose, show } = props;

  const { name, type, umbrellaChecksSupported, selectFieldInformation } =
    useCheckCardContext();
  const { t } = useTranslation();
  const [selectedUmbrellaCheck, setSelectedUmbrellaCheck] = useState<
    components["schemas"]["CheckListChild"] | null
  >(null);

  const shouldFetch = Boolean(show && type);
  const { data: learnMoreData, isLoading } = useClientPortalAPI(
    shouldFetch
      ? `/api/internal/client-portal/customer-accounts/${type}/learn-more/`
      : null
  );

  const shouldFetchChildLearnMore = Boolean(
    selectedUmbrellaCheck?.type && !selectFieldInformation?.length
  );
  const { data: childLearnMoreData, isLoading: isLoadingChildLearnMore } =
    useClientPortalAPI(
      shouldFetchChildLearnMore
        ? `/api/internal/client-portal/customer-accounts/${selectedUmbrellaCheck!.type}/learn-more/`
        : null
    );

  const { data: umbrellaChecks, isLoading: isLoadingUmbrellaChecks } =
    useSelectionEndpointData({
      selectFieldInformation,
      shouldFetch,
      includeChildren: true,
    });

  // Filter out checks that have ordering errors for the customer group
  // Also filter out warnings for HNCCHC checks
  const availableUmbrellaChecks = useMemo(() => {
    return (
      umbrellaChecksSupported?.filter((check) => {
        const errorType =
          check.check_has_ordering_error_for_customer_group?.type;
        return errorType !== "error" && errorType !== "warning";
      }) || []
    );
  }, [umbrellaChecksSupported]);

  const useApiUmbrellaChecks = Boolean(
    !isLoadingUmbrellaChecks &&
      umbrellaChecks?.children &&
      umbrellaChecks.children.length > 0
  );

  let filteredUmbrellaChecks =
    useApiUmbrellaChecks && umbrellaChecks?.children
      ? umbrellaChecks.children.filter((check) => {
          const errorType =
            check.check_has_ordering_error_for_customer_group?.type;
          return errorType !== "error" && errorType !== "warning";
        })
      : availableUmbrellaChecks;
  let renderFullListModal = undefined;

  const isCriminalRecordCheck = type === "CRIMINAL_RECORD_REPORT_1"; // Split into a variable for clarity, can expand later if needed
  if (isCriminalRecordCheck) {
    // Special case for Criminal Record umbrella parent card

    // Filter out international criminal checks (format: AE_CRIMINAL_RECORD_1, GB_CRIMINAL_RECORD_1, etc.)
    // Only show Canadian, UK, and Australian checks which have a different format
    // because they're defined standalone and not intl crim
    filteredUmbrellaChecks = filteredUmbrellaChecks.filter((check) => {
      return !isInternationalCriminalCheck(check.type || "");
    });

    // Pass available checks to the full list modal (filtered to exclude ordering errors and warnings)
    renderFullListModal = (props: { show: boolean; onClose: () => void }) => (
      <CriminalRecordsFullListModal
        checks={
          useApiUmbrellaChecks && umbrellaChecks?.children
            ? umbrellaChecks.children.filter((check) => {
                const errorType =
                  check.check_has_ordering_error_for_customer_group?.type;
                return errorType !== "error" && errorType !== "warning";
              })
            : availableUmbrellaChecks
        }
        show={props.show}
        onClose={props.onClose}
      />
    );
  }

  // Sort filtered checks by display name
  const sortedFilteredUmbrellaChecks = useMemo(() => {
    return [...filteredUmbrellaChecks].sort((a, b) => {
      const aDisplayName = getCheckDisplayNameFromCheck(a, "DROPDOWN");
      const bDisplayName = getCheckDisplayNameFromCheck(b, "DROPDOWN");

      return (aDisplayName || "").localeCompare(bDisplayName || "");
    });
  }, [filteredUmbrellaChecks]);

  if (!learnMoreData) {
    return (
      <Dialog width={560}>
        <DialogHeader onClose={onClose}>{name}</DialogHeader>
        <DialogContent>
          {isLoading || isLoadingUmbrellaChecks ? (
            <Loader />
          ) : (
            <SectionMessage
              type="warning"
              sectionTitle={t("No information available")}
              icon="info"
            >
              {t("No additional information is available for this check.")}
            </SectionMessage>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  const {
    disclaimers_icon_list,
    disclaimers,
    description,
    info_paragraphs,
    key_features,
    how_it_can_help_you,
    umbrella_child_checks_heading,
    coverage,
    estimated_turnaround_time,
    address_based_ordering_coverage,
  } = learnMoreData;

  return (
    <Dialog width={560}>
      {!selectedUmbrellaCheck && (
        <>
          <DialogHeader onClose={onClose}>{name}</DialogHeader>
          <DialogContent>
            {coverage && estimated_turnaround_time && (
              <div className={styles.wrapper}>
                <div className={styles.item}>
                  <Text color="text-weak" style="global/metric-label-small">
                    {t("Turnaround time")}
                  </Text>
                  <Text color="text-base" style="global/metric-small">
                    {estimated_turnaround_time}
                  </Text>
                </div>
                <div className={styles.item}>
                  <Text color="text-weak" style="global/metric-label-small">
                    {t("Coverage")}
                  </Text>
                  <Text color="text-base" style="global/metric-small">
                    {coverage}
                  </Text>
                </div>
              </div>
            )}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--space-3)",
              }}
            >
              <LearnMoreModalSection
                ariaLabel={t("What is it section")}
                title={t("What is it?")}
                content={description}
              />
              {address_based_ordering_coverage &&
                address_based_ordering_coverage.length > 0 && (
                  <LearnMoreModalSection
                    testID="learn-more-modal-address-based-ordering-coverage"
                    ariaLabel={t("Address-based ordering coverage section")}
                    title={t("Address-based ordering coverage")}
                    content={
                      <div style={{ marginTop: "var(--space-1)" }}>
                        {address_based_ordering_coverage.map(
                          (paragraph, index) => (
                            <div key={index}>
                              {index !== 0 && <br />}
                              <Text style="form/body-primary">{paragraph}</Text>
                            </div>
                          )
                        )}
                      </div>
                    }
                  />
                )}
            </div>
            {isLoadingUmbrellaChecks &&
            !sortedFilteredUmbrellaChecks?.length ? (
              <Loader size="small" />
            ) : (
              sortedFilteredUmbrellaChecks?.length > 0 && (
                <UmbrellaChecksSection
                  umbrellaChecksSupported={sortedFilteredUmbrellaChecks}
                  umbrellaChecksHeading={
                    umbrella_child_checks_heading ?? undefined
                  }
                  setSelectedUmbrellaCheck={setSelectedUmbrellaCheck}
                  renderFullListModal={renderFullListModal}
                  fullListCount={
                    useApiUmbrellaChecks && umbrellaChecks?.children
                      ? umbrellaChecks.children.filter((check) => {
                          const errorType =
                            check.check_has_ordering_error_for_customer_group
                              ?.type;
                          return (
                            errorType !== "error" && errorType !== "warning"
                          );
                        }).length
                      : availableUmbrellaChecks.length
                  }
                />
              )
            )}

            <CommonFields
              description={description}
              infoParagraphs={info_paragraphs}
              keyFeatures={key_features}
              disclaimersIconList={disclaimers_icon_list}
              disclaimers={disclaimers}
              howItCanHelpYou={how_it_can_help_you}
            />
          </DialogContent>
          <DialogFooter>
            <PrimaryButton
              size="medium"
              onPress={onClose}
              aria-label={t("Close button")}
              testID="learn-more-modal-close-button"
            >
              {t("Close")}
            </PrimaryButton>
          </DialogFooter>
        </>
      )}
      {selectedUmbrellaCheck && (
        <>
          <DialogHeader onClose={() => setSelectedUmbrellaCheck(null)}>
            {selectedUmbrellaCheck.name}
          </DialogHeader>
          <DialogContent>
            {shouldFetchChildLearnMore ? (
              isLoadingChildLearnMore ? (
                <Loader />
              ) : childLearnMoreData ? (
                <CommonFields
                  description={childLearnMoreData.description}
                  infoParagraphs={childLearnMoreData.info_paragraphs}
                  estimatedTurnaroundTime={
                    childLearnMoreData.estimated_turnaround_time
                  }
                  coverage={childLearnMoreData.coverage}
                  keyFeatures={childLearnMoreData.key_features}
                  disclaimersIconList={childLearnMoreData.disclaimers_icon_list}
                  disclaimers={childLearnMoreData.disclaimers}
                  howItCanHelpYou={childLearnMoreData.how_it_can_help_you}
                />
              ) : (
                <SectionMessage
                  type="warning"
                  sectionTitle={t("No information available")}
                  icon="info"
                >
                  {t("No additional information is available for this check.")}
                </SectionMessage>
              )
            ) : (
              <CommonFields
                description={selectedUmbrellaCheck.description}
                infoParagraphs={selectedUmbrellaCheck.info_paragraphs}
                estimatedTurnaroundTime={
                  selectedUmbrellaCheck.estimated_turnaround_time
                }
                coverage={selectedUmbrellaCheck.coverage}
                keyFeatures={selectedUmbrellaCheck.key_features}
                disclaimersIconList={
                  selectedUmbrellaCheck.disclaimers_icon_list
                }
                disclaimers={selectedUmbrellaCheck.disclaimers}
                howItCanHelpYou={selectedUmbrellaCheck.how_it_can_help_you}
              />
            )}
          </DialogContent>
          <DialogFooter>
            <PrimaryButton
              size="medium"
              onPress={() => setSelectedUmbrellaCheck(null)}
              aria-label={t("Close button")}
              testID="learn-more-modal-close-button"
            >
              {t("Close")}
            </PrimaryButton>
          </DialogFooter>
        </>
      )}
    </Dialog>
  );
}

const styles = {
  preLine: css({
    whiteSpace: "pre-line",
  }),
  wrapper: css({
    display: "flex",
    justifyContent: "space-between",
    boxSizing: "border-box",
    borderRadius: "var(--border-radius-2)",
    padding: "var(--space-3) var(--space-4) var(--space-3) var(--space-4)",
    marginBottom: "var(--space-4)",
    backgroundColor: "var(--background-accent-neutral-weak)",
  }),
  item: css({
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-1)",
  }),
  descriptionBody: css({
    whiteSpace: "pre-line",
  }),
};

export const LearnMoreModalSection = ({
  testID,
  ariaLabel,
  title,
  content,
}: {
  testID?: string;
  ariaLabel: string;
  title: string;
  content: ReactNode;
}) => {
  return (
    <div aria-label={ariaLabel}>
      <Text testID={testID} style="global/heading-large" marginTop="space-4">
        {title}
      </Text>
      <br />
      {typeof content === "string" ? (
        <div style={{ marginTop: "var(--space-1)" }}>
          <Text style="form/body-primary" className={styles.preLine}>
            {content}
          </Text>
        </div>
      ) : (
        content
      )}
    </div>
  );
};
